import {Chart, ChartData, LegendItem, TooltipItem} from 'chart.js';
import chroma from 'chroma-js';
import moment from 'moment';

import {DATASET_COLOR_PROPERTIES} from 'app/components/AnalyzePolygonChart/ByYearChart';
import {getAreaColor, makePointColorCallback} from 'app/components/AnalyzePolygonChart/SimpleChart';
import {DataRange} from 'app/components/AnalyzePolygonChart/types';
import {
  formatUnits,
  getDefaultAverageDataRange,
  getDefaultCarbonDataRange,
  getGraphDataRangeWithBuffer,
} from 'app/components/AnalyzePolygonChart/utils';
import {PieGroup} from 'app/components/Dashboard/GenericChart/PieGroupControls';
import {
  ChartDatasetPoints,
  FeatureMetadataApiResponse,
  FeatureStatProperties,
  FeatureStatsApiResponse,
  MFAResponse,
  OrgNoteStatsApiResponse,
  OrgReportStatsApiResponse,
} from 'app/components/Dashboard/types';
import {ChartConfig, MultifeatureAnalysisConfig} from 'app/src/components/Dashboard/types';
import colors, {hueGen} from 'app/utils/colorUtils';
import {downloadCanvasAsImage} from 'app/utils/domUtils';
import {DataLayerInfo, LayerInfo} from 'app/utils/layers';
import {getLayer} from 'app/utils/layerUtils';
import {getPercentileInterval} from 'app/utils/mathUtils';

export type MultiFeatureScatterDataPoint = {
  x: number;
  y: number;
  date: Date;
  featureId: string;
  featureName: string;
};

export const SMALL_CHART_HEIGHT = 200;
export const LARGE_CHART_HEIGHT = 400;

// Order in which to draw specific datasets
// Lower numbers are drawn on top of higher numbers
export const DRAW_ORDERS = {
  trendLine: 1, // Trendline should be the lowest number
  featureDatasets: 100,
  fillRange: 1000, // Fill range should be the highest number
};

export const MAX_SELECTABLE_FEATURES = 50;
export const MAX_FEATURES_FOR_MULTI_FEATURE_CHART = 20;

const makeMultiFeatureDataPoints = (
  // When using this function for the fill range, we pass in a ChartDatasetPoints object
  // which is a subset of the FeatureStatsApiResponse type - just without undefined values
  featureStats: FeatureStatsApiResponse | ChartDatasetPoints,
  featureId: string,
  timeAggregation: MultifeatureAnalysisConfig['timeAggregation'],
  featureName: string
): MultiFeatureScatterDataPoint[] => {
  if (!featureStats) {
    return [];
  }
  return Object.entries(featureStats)
    .map(([date, value]) => {
      if (value === null || value === undefined) {
        return null;
      }
      // Ensure the date is always the first day of the aggregation period
      // This is important for the data points on the graph to appear correctly
      // with the aggregation period labels
      // HACK: Adding 1 day to the date to avoid timezone issues
      // This will break if the date we get is the last day of the aggregation period
      // but this is temporary fix until we can figure out the UTC vs local timezone issue
      const firstDayOfMonth = moment(date)
        .add(1, 'day')
        .startOf(timeAggregation === 'monthly' ? 'month' : 'year')
        .toDate();
      return {
        x: firstDayOfMonth.getTime(),
        y: value,
        featureId: featureId,
        date: firstDayOfMonth,
        featureName: featureName,
      };
    })
    .filter((point) => point !== null)
    .sort((a, b) => a.x - b.x);
};

/**Used for taking a response to the /statistics endpoint, and converting
 * it into one dataset per feature
 */
export const makeMultiFeatureDatasets = (
  statsByFeature: FeatureStatsApiResponse,
  timeAggregation: MultifeatureAnalysisConfig['timeAggregation']
): ChartData<'line', MultiFeatureScatterDataPoint[]>['datasets'] => {
  const erroredFeatures: string[] = [];
  const colors = generateChartColors(Object.keys(statsByFeature).length);
  const datasets = Object.entries(statsByFeature).map(([featureId, featureStats], index) => {
    if ('error' in featureStats) {
      erroredFeatures.push(featureId);
      return null;
    }

    const color = colors[index];
    const featureName = formattedFeatureName(featureStats.properties);
    return {
      data: makeMultiFeatureDataPoints(featureStats.stats, featureId, timeAggregation, featureName),
      label: featureName,
      fill: false, // To avoid filling the area under the line
      borderColor: color,
      backgroundColor: color,
      spanGaps: true,
      order: DRAW_ORDERS.featureDatasets, // Paint these below the trend line dataset
    };
  });
  if (erroredFeatures.length > 0) {
    console.error(
      `${erroredFeatures.length} features included errors during dataset creation: ${erroredFeatures.join(
        ', '
      )}`
    );
  }
  return datasets.filter((dataset) => dataset !== null);
};

export const getAllSortedTimestamps = (featureStats?: FeatureStatsApiResponse): string[] => {
  if (!featureStats) {
    return [];
  }
  const allTimestamps = new Set<string>();
  Object.values(featureStats).forEach((featureStatsData) => {
    if ('error' in featureStatsData) return;

    Object.keys(featureStatsData.stats).forEach((timestamp) => {
      allTimestamps.add(timestamp);
    });
  });
  return Array.from(allTimestamps).sort();
};

/**Used for taking a response to the /statistics endpoint, and collapsing
 * it down to a single timeseries averaged across all points.
 */
export const calculateTrendLine = (
  featureStats: MFAResponse | undefined,
  trendLineAggregation: 'mean' | 'sum'
): ChartDatasetPoints => {
  if (!featureStats) {
    return {};
  }
  // Get all unique timestamps across all features
  const allTimestamps = getAllSortedTimestamps(featureStats);

  // Calculate average for each timestamp
  const averageValueByDate: ChartDatasetPoints = {};
  const sumByDate: ChartDatasetPoints = {};
  allTimestamps.forEach((timestamp) => {
    let sum = 0;
    let count = 0;

    // Sum up all values for this timestamp
    Object.values(featureStats).forEach((featureStatData) => {
      if ('error' in featureStatData) return;

      const value = featureStatData.stats[timestamp];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    // Calculate and store average
    averageValueByDate[timestamp] = count > 0 ? sum / count : null;
    sumByDate[timestamp] = sum;
  });
  return trendLineAggregation === 'mean' ? averageValueByDate : sumByDate;
};

/**
 * Calculate a fill range (default 90%: 5th to 95th percentile) for each timestamp in the feature stats data.
 */
export const calculateFillRange = (
  featureStats: FeatureStatsApiResponse,
  percentile: number = 0.9
): {lowerBound: ChartDatasetPoints; upperBound: ChartDatasetPoints} => {
  if (!featureStats) {
    return {lowerBound: {}, upperBound: {}};
  }

  const allTimestamps = getAllSortedTimestamps(featureStats);

  // Initialize min and max objects
  const lowerBoundByDate: ChartDatasetPoints = {};
  const upperBoundByDate: ChartDatasetPoints = {};

  allTimestamps.forEach((timestamp) => {
    const valuesAtTimestamp: number[] = [];

    // Collect all values for this timestamp
    Object.values(featureStats).forEach((featureStatData) => {
      if ('error' in featureStatData) return;

      const value = featureStatData.stats[timestamp];
      if (value !== undefined && value !== null) {
        valuesAtTimestamp.push(value);
      }
    });

    if (valuesAtTimestamp.length === 0) {
      lowerBoundByDate[timestamp] = null;
      upperBoundByDate[timestamp] = null;
      return;
    }

    // Calculate indices for 5th and 95th percentiles
    const lowerPercentileCutoff = (1 - percentile) / 2;
    const upperPercentileCutoff = 1 - lowerPercentileCutoff;

    const {lowerBound, upperBound} = getPercentileInterval(valuesAtTimestamp, {
      lower: lowerPercentileCutoff,
      upper: upperPercentileCutoff,
    });

    // Get the percentile values (5th and 95th)
    lowerBoundByDate[timestamp] = lowerBound;
    upperBoundByDate[timestamp] = upperBound;
  });

  return {lowerBound: lowerBoundByDate, upperBound: upperBoundByDate};
};

/**
 * Update dataset colors based on hoveredFeatureId and selectedFeatureIds.
 * Datasets that are hovered or selected will be fully opaque, while others will be transparent.
 *
 * Chart.js unfortunately does not expose opacity properties, so we must modify
 * each color directly. We do that by creating a new hex value with the provided
 * alpha value. Using chroma-js helps maintain resiliency against hex values of
 * different lengths.
 */
export function updateMultiFeatureDatasetColors(
  chart: Chart,
  hoveredFeatureId: string | null,
  selectedFeatureIds: Set<string>
) {
  chart.data.datasets.forEach((dataset) => {
    // Skip trendline datasets as we color them differently
    if (dataset.label === 'Average' || dataset.label === 'Total') {
      return;
    }

    // Get the feature ID from the dataset
    // We need to cast the data point to the correct type
    const dataPoint = dataset.data[0] as MultiFeatureScatterDataPoint;
    const featureId = dataPoint?.featureId;
    if (!featureId) {
      return;
    }

    // Determine if this dataset should be highlighted
    const isHighlighted = hoveredFeatureId === featureId || selectedFeatureIds.has(featureId);

    // Set the alpha value based on whether the dataset is highlighted
    const alpha = isHighlighted ? 1 : 0.1;

    DATASET_COLOR_PROPERTIES.forEach((property) => {
      const color = dataset[property] as string;
      // If a dataset doesn't specify these colors, or the color is 'transparent', do nothing.
      if (!color || color === 'transparent') {
        return;
      }
      dataset[property] = chroma(color).alpha(alpha).hex();
    });
  });
}

export const makeTrendLineChartDatasets = ({
  trendLine,
  layer,
  showFeatureTable,
  timeAggregation,
  percentileRange,
  statAggregation = 'mean',
  hideFill = false,
  hasDataRangeFit = false,
}: {
  trendLine: ChartDatasetPoints;
  layer: DataLayerInfo;
  showFeatureTable: boolean;
  timeAggregation: MultifeatureAnalysisConfig['timeAggregation'];
  percentileRange?: {
    lowerBound: ChartDatasetPoints;
    upperBound: ChartDatasetPoints;
  };
  statAggregation: MultifeatureAnalysisConfig['statAggregation'];
  hideFill?: boolean;
  hasDataRangeFit?: boolean;
}): ChartData<'line', MultiFeatureScatterDataPoint[]>['datasets'] => {
  const pointColorCallback = makePointColorCallback(
    layer.gradientStops,
    layer.dataRange,
    'average',
    layer.key
  );

  const datasets: ChartData<'line', MultiFeatureScatterDataPoint[]>['datasets'] = [];

  if (percentileRange && !hideFill && !hasDataRangeFit) {
    // This is the logic for showing the fill range on the chart.
    // The general strat here is to add a max and min dataset, and then fill between them.
    // We also adjust the fill of the min to be transparent.

    // Get base color
    const baseColor = showFeatureTable
      ? colors.darkGray // Match the average line in table view
      : getAreaColor(layer.gradientStops, layer.dataRange, 'average', layer.key);

    const fillColor = chroma(baseColor).alpha(0.35).hex();
    const borderColor = chroma(baseColor).alpha(0.7).hex();

    // Create min dataset
    const lowerBoundDataset = {
      data: makeMultiFeatureDataPoints(
        percentileRange.lowerBound,
        'lowerBound',
        timeAggregation,
        'lowerBound'
      ),
      label: 'Lower Fill',
      borderColor: borderColor,
      borderWidth: 1,
      pointRadius: 0,
      pointHoverRadius: 0,
      backgroundColor: 'transparent',
      fill: false,
      order: DRAW_ORDERS.fillRange,
    };

    // Create max dataset with fill between
    const upperBoundDataset = {
      data: makeMultiFeatureDataPoints(
        percentileRange.upperBound,
        'upperBound',
        timeAggregation,
        'upperBound'
      ),
      label: 'Upper Fill',
      borderColor: borderColor,
      borderWidth: 1,
      pointRadius: 0,
      pointHoverRadius: 0,
      backgroundColor: fillColor,
      fill: '+1', // Fill between this dataset and the next one
      order: DRAW_ORDERS.fillRange,
    };

    // First add max, then min so the fill works correctly
    datasets.push(upperBoundDataset, lowerBoundDataset);
  }

  // Create average trend line dataset
  const averageTrendLineDataset = {
    data: makeMultiFeatureDataPoints(trendLine, statAggregation, timeAggregation, statAggregation),
    label: statAggregation === 'sum' ? 'Total' : 'Average',
    ...(!showFeatureTable
      ? {
          borderColor: (context) => pointColorCallback(context.parsed?.y),
          backgroundColor: (context) => pointColorCallback(context.parsed?.y),
          pointBorderColor: (context) => pointColorCallback(context.parsed.y),
          pointHoverBackgroundColor: (context) => pointColorCallback(context.parsed.y),
          pointHoverBorderColor: (context) => pointColorCallback(context.parsed.y),
          fill:
            percentileRange || hideFill
              ? false
              : {
                  target: 'start',
                  above: getAreaColor(layer.gradientStops, layer.dataRange, 'average', layer.key),
                },
        }
      : {
          borderDash: [15, 10],
          borderColor: colors.darkestGray,
          backgroundColor: colors.darkestGray,
          pointBorderColor: colors.darkestGray,
          pointHoverBackgroundColor: colors.darkestGray,
          pointHoverBorderColor: colors.darkestGray,
        }),
    order: DRAW_ORDERS.trendLine,
    spanGaps: true,
  };

  datasets.push(averageTrendLineDataset);

  return datasets;
};

// helper function to wrap long text on a canvas
const wrapCanvasText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// calculate required height for wrapped text
const calculateCanvasTextHeight = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontSize: number,
  lineHeight: number = 1.2
): number => {
  ctx.font = `bold ${fontSize}px sans-serif`;
  const lines = wrapCanvasText(ctx, text, maxWidth);
  return lines.length * (fontSize * lineHeight);
};

// Function to generate and return the chartjs canvas with title, description and layer info
// scaleFactor will allow us to scale the canvas up or down for better legibility, ie. 2 = 2x the size
export function formatChartCanvasForDownload(
  originalCanvas: HTMLCanvasElement,
  widgetConfig: MultifeatureAnalysisConfig | ChartConfig,
  layer?: LayerInfo,
  scaleFactor: number = 1,
  legendItems?: LegendItem[] | null | undefined
): HTMLCanvasElement {
  // Create a new canvas with dimensions scaled by the scaleFactor
  // add extra height for the title and description, and potentially legend
  const canvas = document.createElement('canvas');
  const hasLegend = legendItems && legendItems.length > 0;

  const MARGIN = 40; // margins around the canvas
  const TEXT_MARGIN = 8; // small margin between text elements
  const MAX_TEXT_WIDTH = originalCanvas.width * 0.8; // Maximum width for text before wrapping

  //determine how wide our legend columns will be based on the longest item
  const legendColumnWidth = hasLegend ? calculateLegendCanvasWidth(legendItems) * 1.6 : 0;
  const itemsPerColumn = 8;

  const smallFontSize = scaleFactor === 1 ? 12 : 16;
  const mediumFontSize = scaleFactor === 1 ? 16 : 20;
  const largeFontSize = scaleFactor === 1 ? 20 : 24;

  // add extra width for side by side chart+legend
  const extraColumnsCount = hasLegend
    ? Math.max(Math.ceil(legendItems.length / itemsPerColumn), 1)
    : 0;

  // create a temporary canvas to measure text
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) {
    throw new Error('Failed to get temporary canvas context');
  }

  // Calculate heights for title and description
  const titleHeight = calculateCanvasTextHeight(
    tempCtx,
    widgetConfig.title,
    MAX_TEXT_WIDTH,
    largeFontSize
  );

  const descriptionHeight = widgetConfig.description
    ? calculateCanvasTextHeight(tempCtx, widgetConfig.description, MAX_TEXT_WIDTH, mediumFontSize)
    : 0;
  const layerInfoHeight = layer && 'featureIds' in widgetConfig ? smallFontSize * 1.2 : 0;

  // Calculate total extra height needed for text
  const extraHeight = titleHeight + descriptionHeight + layerInfoHeight + MARGIN * 2;

  // set the dimensions of our new canvas with extra stuff
  canvas.width =
    (originalCanvas.width + MARGIN * 2 + legendColumnWidth * extraColumnsCount) * scaleFactor;
  canvas.height = (originalCanvas.height + extraHeight) * scaleFactor;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Apply the scaleFactor for everything
  ctx.scale(scaleFactor, scaleFactor);

  // Fill with white background first, otherwise there is no background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width / scaleFactor, canvas.height / scaleFactor);

  // Draw text with wrapping
  let currentY = MARGIN;

  // Draw title
  ctx.fillStyle = '#000000';
  ctx.font = `bold ${largeFontSize}px sans-serif`;
  ctx.textAlign = 'left';
  const titleLines = wrapCanvasText(ctx, widgetConfig.title, MAX_TEXT_WIDTH);
  titleLines.forEach((line) => {
    ctx.fillText(line, MARGIN, currentY + largeFontSize);
    currentY += largeFontSize * 1.2;
  });

  // Add margin between title and description
  currentY += TEXT_MARGIN;

  // Draw description
  if (widgetConfig.description) {
    ctx.font = `${mediumFontSize}px sans-serif`;
    const descriptionLines = wrapCanvasText(ctx, widgetConfig.description, MAX_TEXT_WIDTH);
    descriptionLines.forEach((line) => {
      ctx.fillText(line, MARGIN, currentY + mediumFontSize);
      currentY += mediumFontSize * 1.2;
    });
  }

  // Draw layer info
  if (layer && 'featureIds' in widgetConfig) {
    ctx.font = `${smallFontSize}px sans-serif`;
    ctx.fillText(
      `${layer.display} | ${widgetConfig.featureIds?.length || 'all'} properties`,
      MARGIN,
      currentY + smallFontSize
    );
    currentY += smallFontSize * 1.2;
  }

  // Draw the original canvas content (ie the chart) below the text
  ctx.drawImage(originalCanvas, MARGIN, currentY);

  // If we have legend items, render them on the canvas, to the right of the chart
  if (legendItems && legendItems.length > 0) {
    const fontSize = smallFontSize;
    const fontStyle = 'normal';
    const itemHeight = 20;
    const itemMargin = 10;

    ctx.font = `${fontStyle} ${fontSize}px sans-serif`;

    const legendY = currentY;
    const legendX = originalCanvas.width + MARGIN * 2;

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'left';

    let currentX = legendX;
    let currentLegendY = legendY;

    legendItems.forEach((item, index) => {
      if (item.hidden) return;

      // Color box
      ctx.fillStyle = item.fillStyle as string;
      ctx.fillRect(currentX, currentLegendY - itemHeight / 2, itemHeight, itemHeight);

      // Text
      ctx.fillStyle = '#000000';
      ctx.fillText(item.text, currentX + itemHeight + itemMargin, currentLegendY + fontSize / 3);

      // Move to next position
      currentLegendY += itemHeight + itemMargin;

      if (index % itemsPerColumn === itemsPerColumn - 1) {
        currentX += legendColumnWidth;
        currentLegendY = legendY;
      }
    });
  }

  // Restore the original context state
  ctx.restore();

  return canvas;
}

export const formatAggregatedDate = (
  date: string,
  aggregation: MultifeatureAnalysisConfig['timeAggregation']
) => {
  if (aggregation === 'monthly') {
    return moment.utc(date).format('MMM YYYY');
  }
  return moment.utc(date).format('YYYY');
};

export function formattedFeatureName(featureStatProperties: FeatureStatProperties): string {
  const {name, multiFeaturePartName} = featureStatProperties;
  return multiFeaturePartName ? `${name} - ${multiFeaturePartName}` : name;
}

export function formatChartType(widgetConfig: MultifeatureAnalysisConfig) {
  switch (widgetConfig.statAggregation) {
    case 'mean':
      return 'Average value';
    case 'sum':
      if (widgetConfig.displayOptions?.trendLineAggregation === 'mean') {
        return 'Average value';
      }
      return 'Aggregate value';
    case 'area_counts':
      return 'Area by category';
    default:
      return widgetConfig.statAggregation;
  }
}

// heuristic to calculate the width of the legend columns based on the longest item
export const calculateLegendCanvasWidth = (legendItems: LegendItem[]) => {
  const narrowLegendWidth = 160;
  const mediumLegendWidth = 200;
  const wideLegendWidth = 240;

  const longestItem = [...legendItems].sort((a, b) => b.text.length - a.text.length)[0].text.length;
  if (longestItem <= 30) return narrowLegendWidth;
  if (longestItem <= 40) return mediumLegendWidth;
  return wideLegendWidth;
};

export const handleDownloadPng = (
  chart: HTMLCanvasElement | null | undefined,
  widgetConfig: MultifeatureAnalysisConfig | ChartConfig,
  layer?: LayerInfo,
  isExpanded?: boolean,
  onError?: () => void,
  legendItems?: LegendItem[] | undefined | null
) => {
  if (!chart) return;

  // Find the canvas element inside the chart
  const originalCanvas = chart;
  if (!originalCanvas) {
    onError?.();
    console.error('No canvas element found in the chart');
    return;
  }

  // if we want to control chart size in these pngs we can use this
  // but for now just render the size of the original canvas
  const hasLegend = legendItems && legendItems.length > 0;
  const scaleFactor = hasLegend || isExpanded ? 1 : 2;

  try {
    const canvas = formatChartCanvasForDownload(
      originalCanvas,
      widgetConfig,
      layer,
      scaleFactor,
      legendItems
    );

    const filename = `${widgetConfig.title.replace(/\s+/g, '_')}_chart.png`; // regex replace spaces with underscores
    downloadCanvasAsImage(canvas, filename);
  } catch (error) {
    onError?.();
    console.error('Error downloading chart as PNG:', error);
  }
};

/**
 * Generates an array of visually distinct RGBA color strings suitable for charts.
 *
 * This function takes the number of colors needed and generates them using
 * the `hueGen` helper for maximum contrast. For each hue, it creates two related
 * colors (one slightly lighter, one slightly darker) suitable for chart backgrounds
 * and borders.
 *
 * Use this when you need a set of different colors for chart datasets but
 * don't want to specify them manually.
 *
 * @param {number} count The number of colors to generate.
 * @param {number} [offset=0] Optional number of initial colors to skip.
 * @returns {string[]} An array of RGBA color strings (e.g., 'rgba(54, 162, 235, 0.75)').
 */
export function generateChartColors(count: number, offset = 0): string[] {
  const colors: string[] = [];
  const hue = hueGen();

  // Apply offset if provided, skipping the first `offset` hues
  for (let i = 0; i < offset; i++) {
    hue.next();
  }

  for (let i = 0; i < count; i++) {
    // Get the next hue value (0-1) and convert it to degrees (0-360)
    const h = (hue.next().value as number) * 360; // Ensure value is treated as number
    // Generate two related colors using the same hue but different lightness/saturation
    // Alpha (transparency) is set to 192/255 (approx 75% opaque)
    const color1 = chroma
      .hsv(h, 0.6, 0.8)
      .alpha(192 / 255)
      .hex('rgba'); // Lighter version
    const color2 = chroma
      .hsv(h, 0.6, 0.5)
      .alpha(192 / 255)
      .hex('rgba'); // Darker version

    // Alternate between the lighter and darker versions
    if (i % 2 === 0) {
      colors.push(color1);
      // If we need an odd number of colors and this is the last one,
      // we still need to advance the hue generator once more to consume the pair.
      if (i + 1 >= count) hue.next();
    } else {
      colors.push(color2);
    }
  }
  return colors;
}

/**
 * Processes pie chart entries by taking the top N entries and grouping the rest into "Other"
 * @param entries Array of tuples containing [id, value]
 * @param maxEntries Maximum number of entries to show before grouping into "Other"
 * @param shouldExpand Whether to show all entries or group into "Other"
 * @returns Processed entries with "Other" category if needed
 */
export const makeTopPieChartEntries = (
  entries: [string, number][],
  maxEntries: number = 5,
  showAll: boolean = false
): [string, number][] => {
  // Sort entries by value (descending)
  const sortedEntries = [...entries].sort(([, valueA], [, valueB]) => valueB - valueA);

  if (!showAll && sortedEntries.length > maxEntries) {
    // Take top N entries and sum the rest into "Other"
    const topEntries = sortedEntries.slice(0, maxEntries);
    const otherSum = sortedEntries.slice(maxEntries).reduce((sum, [, value]) => sum + value, 0);
    return [...topEntries, ['Other', otherSum]];
  }

  return sortedEntries;
};

/**
 * Formats the tooltip text for pie charts based on the selected grouping type.
 * @param context The Chart.js tooltip context
 * @param selectedPieGroup The currently selected pie group configuration
 * @param countItemLabel The label to display for the count of items (eg "properties" or "acres")
 * @returns Formatted tooltip text with optional percentage
 */
export function formatPieChartTooltip(
  context: TooltipItem<'pie'>,
  selectedPieGroup: PieGroup,
  countItemLabel: string
): string[] {
  const label = context.label || '';
  const value = context.raw as number;
  const total = context.dataset.data.reduce(
    (acc, val) => acc + (typeof val === 'number' ? val : 0),
    0
  );
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

  return [
    label,
    `${value.toLocaleString()} ${countItemLabel}${
      selectedPieGroup.singleMembership ? ` (${percentage}%)` : ''
    }`,
  ];
}

/**
 * Returns a formatted data point y-value.
 * A mirror of formatYValue in AnalyzePolygonChart/utils.ts
 *
 * TODO: extend this to include area_counts and handle units?
 */
export function formatYValue(
  value: number,
  statAggregation: MultifeatureAnalysisConfig['statAggregation'],
  layerInfo: DataLayerInfo
) {
  switch (statAggregation) {
    case 'sum':
      return formatUnits(value, layerInfo.dataUnit);

    case 'mean':
    default:
      return value.toFixed(2);
  }
}

export function getDataRange(
  widgetConfig: MultifeatureAnalysisConfig,
  datasets: ChartData<'line', MultiFeatureScatterDataPoint[]>['datasets'],
  hasDataRangeFit: boolean
) {
  const allValues = datasets
    .flatMap((dataset) => dataset.data)
    .map((point) => point.y as number)
    .filter((value) => !isNaN(value));
  const defaultDataRange = getDefaultDataRange(widgetConfig, allValues);
  if (hasDataRangeFit) {
    const fitDataRange = getGraphDataRangeWithBuffer(allValues);
    // Sometimes when buffering out the data, we get values that are
    // outside the range of the data. So instead of shrinking our range, we
    // end up growing it a bit. Prevent that from happening by clamping
    // the fit data range to the default data range.
    const fitDataRangeWithinDefault = [
      Math.max(fitDataRange[0], defaultDataRange[0]),
      Math.min(fitDataRange[1], defaultDataRange[1]),
    ];
    return fitDataRangeWithinDefault;
  } else {
    return defaultDataRange;
  }
}

export function getDefaultDataRange(
  widgetConfig: MultifeatureAnalysisConfig,
  values: number[]
): DataRange {
  // If we have set a data range on the widget config, that takes precedence
  // over the default data range options set on each chart type
  if (widgetConfig.displayOptions?.dataRange) {
    return widgetConfig.displayOptions.dataRange;
  }

  switch (widgetConfig.statAggregation) {
    case 'sum':
      return getDefaultCarbonDataRange(values);

    case 'mean':
      return getDefaultAverageDataRange(
        (getLayer(widgetConfig.layerKey) as DataLayerInfo).dataRange
      );

    case 'area_counts':
    default:
      return [0, 1];
  }
}

export const EMPTY_PIE_CHART_DATA: ChartData<'pie'> = {
  labels: ['No data'],
  datasets: [{data: [1], backgroundColor: '#E5E7EB'}],
} as ChartData<'pie'>;

/**
 * Creates pie chart data from various API response types, grouping by portfolio or tag.
 * @param data The API response data to process
 * @param selectedPieGroup The pie group configuration to determine grouping
 * @returns Chart.js pie chart data
 */
export function makePieChartCountData<
  T extends FeatureMetadataApiResponse[] | OrgNoteStatsApiResponse[] | OrgReportStatsApiResponse[],
>(data: T | undefined, selectedPieGroup: PieGroup): ChartData<'pie'> {
  if (!data) {
    return EMPTY_PIE_CHART_DATA;
  }

  const counts: Record<string, number> = {};
  const labelMap: Record<string, string> = {};

  // Process each item in the data array
  data.forEach(
    (item: FeatureMetadataApiResponse | OrgNoteStatsApiResponse | OrgReportStatsApiResponse) => {
      Object.values(item).forEach((entry) => {
        // sometimes our data items can belong to more than one pie slice, eg, tags. so check if
        // we tryna break out by an array, and if so, map over that.
        const itemsToAddToPieSlices = Array.isArray(selectedPieGroup.groupingField)
          ? entry[selectedPieGroup.groupingField]?.map((subItem: string) => subItem)
          : // otherwise, if we just adding a single data item to a single pie slice, just grab the
            // single data item's grouping field.
            [entry[selectedPieGroup.groupingField]];

        // we sometimes get inconsistent values for tags (nullish, or empty arrays)
        if (!itemsToAddToPieSlices || itemsToAddToPieSlices.every((item) => !item)) return;

        itemsToAddToPieSlices.forEach((item) => {
          // if we're giving an item of things to add to a pie slice, add each of those to a pie slice
          Array.isArray(item)
            ? item.forEach((subItem) => {
                counts[subItem] = (counts[subItem] || 0) + 1;
                labelMap[subItem] = subItem;
              })
            : (counts[item] = (counts[item] || 0) + 1);
          // otherwise, add the label field (bc it might be different from the grouping field)
          labelMap[item] = entry[selectedPieGroup.labelField];
        });
      });
    }
  );

  const labels = Object.keys(counts).map((id) => labelMap[id]);
  const values = Object.values(counts);
  const colors = generateChartColors(values.length);

  if (values.length === 0) {
    return EMPTY_PIE_CHART_DATA;
  }

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      },
    ],
  };
}

// peels the percentage loaded off the lens wrapped data,
// returning undefined to make spinner just spin if no data yet
/*
 * Calculates the percentage of data loaded from a Lens Wrapped API response.
 * The response contains a list of dictionaries, where each dictionary is keyed by an ID
 * and contains metadata including a totalCount property. This function extracts the
 * totalCount from the first item to determine the loading progress.
 */

export const getLensStatsPercentLoaded = (
  data:
    | FeatureMetadataApiResponse[]
    | OrgNoteStatsApiResponse[]
    | OrgReportStatsApiResponse[]
    | undefined
) => {
  if (!data || !data.length) return undefined;

  const firstItem = data[0];
  const firstItemDict = Object.values(firstItem)[0];
  const totalCount = firstItemDict?.totalCount;
  if (!totalCount) return undefined;

  const percentLoaded = data.length / totalCount;
  return percentLoaded;
};

export const getTrendLineAggregation = (widgetConfig: MultifeatureAnalysisConfig) => {
  if (widgetConfig.displayOptions?.trendLineAggregation) {
    return widgetConfig.displayOptions.trendLineAggregation;
  }
  if (widgetConfig.statAggregation === 'mean') {
    return 'mean';
  }
  return 'sum';
};

export const getSumMeanLegendItems = ({
  data,
  displayFeatureIds,
  statType,
}: {
  data: FeatureStatsApiResponse;
  displayFeatureIds: Set<string>;
  layerKey: string;
  statType: string;
}): LegendItem[] => {
  const dataColors = generateChartColors(Object.keys(data).length);

  return Array.from(displayFeatureIds).map((id, index) => {
    let text = '';
    if (id === 'trendline') {
      text = statType === 'sum' ? 'Total' : 'Average';
    } else if (data[id]?.['properties']) {
      text = data[id]['properties'].name;
    }
    return {
      label: id,
      text: text,
      datasetIndex: index,
      fillStyle: dataColors[Object.keys(data).findIndex((d) => d === id)],
    };
  });
};
